// Adhoc display — single-page stage machine for UC1 booth devices.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobile, isTablet, SessionState, type EnabledVitalSigns } from '@biosensesignal/web-sdk';
import {
  ADHOC_DONE_RESET_MS,
  ADHOC_IDLE_TIMEOUT_MS,
  ADHOC_PAYMENT_CONFIRM_MAX_POLLS,
  ADHOC_PAYMENT_CONFIRM_POLL_MS,
  ADHOC_PAYMENT_FAIL_RESET_MS,
  ADHOC_STAFF_PIN,
  resolveAdhocId,
} from '../../config/adhocTiming';
import { ADHOC_SCAN_MAINTENANCE } from '../../config/featureFlags';
import {
  useCameras,
  useDisableZoom,
  useLicenseKey,
  useMeasurementDuration,
  usePageVisibility,
  useResolvedScanAlert,
} from '../../hooks';
import { AlertActionType } from '../../alerts/alertTypes';
import AdhocSettingsBars from '../AdhocSettingsBars';
import {
  acceptAdhocDisclaimer,
  createAdhocSession,
  getAdhocConfig,
  getAdhocPack,
  getAdhocPackByCheckout,
  getAdhocSession,
  accessAdhocPackByToken,
  updateAdhocProfile,
  saveAdhocScan,
  sendAdhocReport,
  type AdhocBoothConfig,
  type AdhocPackInfo,
} from '../../api/adhocApi';
import useAdhocMonitor from '../../hooks/useAdhocMonitor';
import Mask from '../../assets/mask.svg';

import { ADHOC_DISCLAIMER_VERSION } from '../../content/adhocDisclaimer';
import AdhocDisclaimerStage from './AdhocDisclaimerStage';
import AdhocInstructionsStage from './AdhocInstructionsStage';
import AdhocLandingStage from './AdhocLandingStage';
import AdhocPaymentConfirmStage from './AdhocPaymentConfirmStage';
import AdhocPaymentFailedStage from './AdhocPaymentFailedStage';
import AdhocProfileStage, { AdhocProfileData } from './AdhocProfileStage';
import AdhocScanStage from './AdhocScanStage';
import AdhocEmailSuccessStage from './AdhocEmailSuccessStage';
import AdhocErrorPanel from './AdhocErrorPanel';
import AdhocPackHubStage from './AdhocPackHubStage';
import AdhocMaintenanceStage from './AdhocMaintenanceStage';
import {
  clearSavedAdhocPack,
  readSavedAdhocPack,
  saveAdhocPack,
  readPendingPackCheckout,
  clearPendingPackCheckout,
  clearPendingGuestCheckout,
} from './adhocPackStorage';
import {
  adhocProfileToHealthProfile,
  toSdkUserInformation,
  type HealthProfile,
} from '../../utils/userProfile';
import { serializeEnabledVitals } from '../../utils/vitalsMetadata';
import {
  clearAdhocFlowSnapshot,
  readAdhocFlowSnapshot,
  sessionToHealthProfile,
  writeAdhocFlowSnapshot,
  type PersistedStage,
} from './adhocSessionStorage';
import {
  adhocLog,
  clearAdhocLogBuffer,
  downloadAdhocDebugLog,
  setAdhocLogBooth,
  setAdhocLogSession,
} from '../../utils/adhocLogger';

type Stage =
  | 'landing'
  | 'pack_hub'
  | 'pack_confirming'
  | 'confirming_payment'
  | 'payment_failed'
  | 'disclaimer'
  | 'profile'
  | 'scan_instructions'
  | 'scanning'
  | 'email_success'
  | 'maintenance';

const SPIN_STYLE = `@keyframes spin{to{transform:rotate(360deg)}}`;
const STAFF_TAP_COUNT = 5;
const STAFF_TAP_WINDOW_MS = 3000;

function stripPaymentQueryParams(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('session');
  params.delete('payment');
  params.delete('pack_checkout');
  params.delete('pack_payment');
  params.delete('pack_provider');
  params.delete('reference');
  params.delete('status');
  params.delete('pack_access');
  params.delete('session_provider');
  return params.toString();
}

function buildPackSearch(adhocId: string, packCode: string): string {
  const params = new URLSearchParams();
  params.set('adhoc_id', adhocId);
  params.set('pack', packCode);
  return params.toString();
}

function isPackGoneError(e: unknown): boolean {
  const status = (e as { status?: number })?.status;
  return status === 404 || status === 410;
}

function packApiErrorMessage(e: unknown): string {
  const detail = (e as { detail?: string })?.detail;
  if (typeof detail === 'string') return detail;
  return 'Family pack not found or no longer available.';
}

function resolveOwnerTokenForPack(code: string, adhocId: string): string | undefined {
  const saved = readSavedAdhocPack();
  if (saved?.packCode === code && saved.adhocId === adhocId && saved.ownerToken) {
    return saved.ownerToken;
  }
  return undefined;
}

function packCreditsRemaining(pack: AdhocPackInfo): number {
  return pack.scans_remaining;
}

function buildPackDisplayPath(adhocId: string, shareCode: string): string {
  return `/display?adhoc_id=${encodeURIComponent(adhocId)}&pack=${encodeURIComponent(shareCode)}`;
}

export default function AdhocDisplayApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const adhocId = resolveAdhocId(location.search);
  const multiScanHref = `/display/family-pack?adhoc_id=${encodeURIComponent(adhocId)}`;
  const singleScanHref = `/display/checkout?adhoc_id=${encodeURIComponent(adhocId)}`;

  const syncDisplaySearch = useCallback((search: string) => {
    navigate(
      { pathname: '/display', search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [navigate]);

  const [stage, setStage] = useState<Stage>('landing');
  const [boothConfig, setBoothConfig] = useState<AdhocBoothConfig | null>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);

  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paymentPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paymentReturnHandledRef = useRef(false);
  const sessionRecoveryAttemptedRef = useRef(false);
  const idleCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef(Date.now());

  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [packCode, setPackCode] = useState<string | null>(null);
  const [packInfo, setPackInfo] = useState<AdhocPackInfo | null>(null);
  const [scanCreditsRemaining, setScanCreditsRemaining] = useState<number | null>(null);
  const packStripeReturnHandledRef = useRef<string | null>(null);
  const packPersistenceHandledRef = useRef(false);
  const packHubOpeningRef = useRef<string | null>(null);
  const packOpenBlockedRef = useRef<string | null>(null);
  const [scanSaveError, setScanSaveError] = useState<string | null>(null);
  const [emailSendError, setEmailSendError] = useState<string | null>(null);
  const [postScanMessage, setPostScanMessage] = useState('Saving your results…');
  const [flowError, setFlowError] = useState<string | null>(null);

  const [guestName, setGuestName] = useState('');
  const [healthProfile, setHealthProfile] = useState<Partial<HealthProfile> | null>(null);
  const [scanInitKey, setScanInitKey] = useState(0);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [disclaimerSaving, setDisclaimerSaving] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState<string | null>(null);
  const [instructionsContinuing, setInstructionsContinuing] = useState(false);

  const shouldPreWarmSdk = !ADHOC_SCAN_MAINTENANCE && (
    stage === 'confirming_payment' || stage === 'disclaimer' || stage === 'profile'
    || stage === 'scan_instructions' || stage === 'scanning'
  );
  const shouldAccessCamera = !ADHOC_SCAN_MAINTENANCE && stage === 'scanning';
  const { cameras, ready: camerasReady, refresh: refreshCameras } = useCameras({
    enabled: shouldAccessCamera,
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [staffPinPrompt, setStaffPinPrompt] = useState(false);
  const [staffPinInput, setStaffPinInput] = useState('');
  const staffTapTimesRef = useRef<number[]>([]);

  const [cameraId, setCameraId] = useState<string | undefined>();
  const [awaitingCameraEnable, setAwaitingCameraEnable] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [startMeasuring, setStartMeasuring] = useState(false);
  const [isMeasurementEnabled, setIsMeasurementEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(false);
  const video = useRef<HTMLVideoElement>(null);
  const [loadingTimeout, setLoadingTimeout] = useState<number>();

  const [processingTime] = useMeasurementDuration();
  const [licenseKey] = useLicenseKey();
  const isPageVisible = usePageVisibility();
  const desktop = useMemo(() => !isTablet() && !isMobile(), []);
  useDisableZoom();

  const touchActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const clearTimers = useCallback(() => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    resetTimerRef.current = null;
    paymentPollRef.current = null;
  }, []);

  const resetToLanding = useCallback(() => {
    clearTimers();
    clearAdhocFlowSnapshot();
    adhocLog('app', 'info', 'Reset to landing');
    setAdhocLogSession(null, adhocId);
    clearAdhocLogBuffer();
    paymentReturnHandledRef.current = false;
    setSessionId(null);
    setSessionEmail(null);
    setGuestName('');
    setHealthProfile(null);
    setScanInitKey(0);
    setCameraId(undefined);
    setSaving(false);
    savedRef.current = false;
    setStartMeasuring(false);
    setIsLoading(false);
    setVideoReady(false);
    setAwaitingCameraEnable(false);
    setPaying(false);
    setPayError(null);
    setScanSaveError(null);
    setEmailSendError(null);
    setPostScanMessage('Saving your results…');
    setFlowError(null);
    setProfileError(null);
    setDisclaimerError(null);
    setDisclaimerSaving(false);
    setInstructionsContinuing(false);
    setPackInfo(null);
    setPackCode(null);
    packHubOpeningRef.current = null;
    lastActivityRef.current = Date.now();
    setStage('landing');
    syncDisplaySearch(`adhoc_id=${encodeURIComponent(adhocId)}`);
  }, [clearTimers, adhocId, syncDisplaySearch]);

  const refreshPackInfo = useCallback(async (code: string, ownerToken?: string) => {
    const p = await getAdhocPack(code, ownerToken);
    setPackInfo(p);
    if (ownerToken) {
      if (p.scans_remaining <= 0) {
        clearSavedAdhocPack();
        setScanCreditsRemaining(null);
      } else {
        saveAdhocPack(p.adhoc_id, p.share_code, ownerToken);
        setScanCreditsRemaining(packCreditsRemaining(p));
      }
    } else {
      setScanCreditsRemaining(packCreditsRemaining(p));
    }
    return p;
  }, []);

  const openPackHub = useCallback(async (code: string) => {
    if (packHubOpeningRef.current === code) return;
    packHubOpeningRef.current = code;
    packOpenBlockedRef.current = null;
    setPackCode(code);
    setPayError(null);
    touchActivity();
    try {
      const ownerToken = resolveOwnerTokenForPack(code, adhocId);
      const p = await refreshPackInfo(code, ownerToken);
      setStage('pack_hub');
      syncDisplaySearch(buildPackSearch(p.adhoc_id, p.share_code));
    } catch (e: unknown) {
      packHubOpeningRef.current = null;
      if (isPackGoneError(e)) {
        clearSavedAdhocPack();
        setScanCreditsRemaining(null);
        packOpenBlockedRef.current = null;
        syncDisplaySearch(`adhoc_id=${encodeURIComponent(adhocId)}`);
      } else {
        packOpenBlockedRef.current = code;
      }
      setPayError(packApiErrorMessage(e));
      setStage('landing');
    }
  }, [adhocId, refreshPackInfo, syncDisplaySearch, touchActivity]);

  const handleOpenMyFamilyPack = useCallback(async () => {
    const saved = readSavedAdhocPack();
    if (saved?.packCode && saved.adhocId === adhocId) {
      openPackHub(saved.packCode);
    }
  }, [adhocId, openPackHub]);

  const returnToPackHub = useCallback(async (code: string) => {
    clearTimers();
    clearAdhocFlowSnapshot();
    setSessionId(null);
    setSessionEmail(null);
    setGuestName('');
    setHealthProfile(null);
    setScanInitKey(0);
    setPaying(false);
    setPayError(null);
    await openPackHub(code);
  }, [clearTimers, openPackHub]);

  const goToPostPaymentStage = useCallback((session: Awaited<ReturnType<typeof getAdhocSession>>) => {
    if (session.email) setSessionEmail(session.email);
    touchActivity();
    setStage(ADHOC_SCAN_MAINTENANCE ? 'maintenance' : 'disclaimer');
  }, [touchActivity]);

  const startPaymentConfirmPoll = useCallback((session: string) => {
    if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    let attempts = 0;

    paymentPollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const s = await getAdhocSession(session);
        if (s.is_expired) {
          clearTimers();
          setPayError('This session has expired. Please start again.');
          setStage('landing');
          return;
        }
        if (s.status === 'paid' || s.status === 'scanned' || s.status === 'report_sent') {
          clearTimers();
          goToPostPaymentStage(s);
        }
      } catch {
        /* transient network errors */
      }
      if (attempts >= ADHOC_PAYMENT_CONFIRM_MAX_POLLS) {
        clearTimers();
        setPayError('Payment confirmation is taking longer than expected. Please try again.');
        setStage('landing');
      }
    }, ADHOC_PAYMENT_CONFIRM_POLL_MS);
  }, [clearTimers, goToPostPaymentStage]);

  useEffect(() => {
    if (paymentReturnHandledRef.current) return;

    const params = new URLSearchParams(location.search);
    const sessionProvider = params.get('session_provider');

    if (sessionProvider === 'hitpay') {
      const hitpayStatus = (params.get('status') || '').toLowerCase();
      const returnSessionId = params.get('session');
      const reference = params.get('reference');

      if (returnSessionId && reference && hitpayStatus === 'completed') {
        paymentReturnHandledRef.current = true;
        syncDisplaySearch(stripPaymentQueryParams(location.search));
        setSessionId(returnSessionId);
        touchActivity();
        clearPendingGuestCheckout();
        setStage('confirming_payment');
        startPaymentConfirmPoll(returnSessionId);
        return;
      }

      if (returnSessionId && (reference || hitpayStatus)) {
        paymentReturnHandledRef.current = true;
        clearPendingGuestCheckout();
        syncDisplaySearch(stripPaymentQueryParams(location.search));
        setPayError('Payment was not completed. Please try again.');
        setStage('landing');
        return;
      }
    }

    const returnSessionId = params.get('session');
    const payment = params.get('payment');
    if (!returnSessionId || !payment) return;

    paymentReturnHandledRef.current = true;
    syncDisplaySearch(stripPaymentQueryParams(location.search));
    setSessionId(returnSessionId);
    touchActivity();

    if (payment === 'cancelled') {
      clearAdhocFlowSnapshot();
      setStage('payment_failed');
      return;
    }

    if (payment === 'success') {
      setStage('confirming_payment');
      getAdhocSession(returnSessionId)
        .then((s) => {
          if (s.status === 'paid' || s.status === 'scanned' || s.status === 'report_sent') {
            goToPostPaymentStage(s);
            return;
          }
          if (s.is_expired) {
            setPayError('This session has expired. Please start again.');
            setStage('landing');
            return;
          }
          startPaymentConfirmPoll(returnSessionId);
        })
        .catch(() => {
          startPaymentConfirmPoll(returnSessionId);
        });
    }
  }, [goToPostPaymentStage, location.search, startPaymentConfirmPoll, syncDisplaySearch, touchActivity]);

  const startPackConfirmPoll = useCallback((
    checkoutId: string,
    provider: 'stripe' | 'hitpay' = 'stripe',
  ) => {
    if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    let attempts = 0;

    paymentPollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const p = await getAdhocPackByCheckout(checkoutId, provider);
        if (!p.owner_token) return;
        setPackCode(p.share_code);
        setPackInfo(p);
        saveAdhocPack(p.adhoc_id, p.share_code, p.owner_token);
        setScanCreditsRemaining(packCreditsRemaining(p));
        clearPendingPackCheckout();
        clearTimers();
        syncDisplaySearch(buildPackSearch(p.adhoc_id, p.share_code));
        setStage('pack_hub');
      } catch {
        /* pack not ready yet */
      }
      if (attempts >= ADHOC_PAYMENT_CONFIRM_MAX_POLLS) {
        clearTimers();
        clearPendingPackCheckout();
        setPayError('Pack purchase confirmation is taking longer than expected.');
        setStage('landing');
      }
    }, ADHOC_PAYMENT_CONFIRM_POLL_MS);
  }, [clearTimers, syncDisplaySearch]);

  // Restore saved pack credits, email pack_access links, and in-flight checkouts on mount.
  useEffect(() => {
    if (packPersistenceHandledRef.current) return;
    packPersistenceHandledRef.current = true;

    const params = new URLSearchParams(location.search);
    const hasPackReturn = Boolean(
      params.get('pack_checkout')
      || params.get('reference')
      || params.get('pack_payment')
      || params.get('pack_access'),
    );

    const packAccess = params.get('pack_access');
    if (packAccess) {
      let cancelled = false;
      accessAdhocPackByToken(packAccess)
        .then((p) => {
          if (cancelled || !p.owner_token) return;
          saveAdhocPack(p.adhoc_id, p.share_code, p.owner_token);
          setScanCreditsRemaining(packCreditsRemaining(p));
          setPackCode(p.share_code);
          setPackInfo(p);
          syncDisplaySearch(buildPackSearch(p.adhoc_id, p.share_code));
          setStage('pack_hub');
        })
        .catch(() => {
          if (!cancelled) {
            setPayError('This pack link is invalid or has expired.');
          }
        });
      return () => {
        cancelled = true;
      };
    }

    const saved = readSavedAdhocPack();
    if (saved?.ownerToken && saved.adhocId === adhocId) {
      getAdhocPack(saved.packCode, saved.ownerToken)
        .then((p) => {
          if (p.scans_remaining <= 0) {
            clearSavedAdhocPack();
            setScanCreditsRemaining(null);
            return;
          }
          saveAdhocPack(p.adhoc_id, p.share_code, saved.ownerToken);
          setScanCreditsRemaining(packCreditsRemaining(p));
        })
        .catch((e: unknown) => {
          if (isPackGoneError(e)) {
            clearSavedAdhocPack();
            setScanCreditsRemaining(null);
          }
        });
    } else {
      setScanCreditsRemaining(null);
    }

    if (!hasPackReturn) {
      const pending = readPendingPackCheckout();
      if (pending?.adhocId === adhocId) {
        setStage('pack_confirming');
        startPackConfirmPoll(pending.checkoutId, pending.provider);
      }
    }
  }, [adhocId, location.search, startPackConfirmPoll, syncDisplaySearch]);

  // Refresh remaining scan count whenever the landing page is shown.
  useEffect(() => {
    if (stage !== 'landing') return;
    const saved = readSavedAdhocPack();
    if (!saved?.ownerToken || saved.adhocId !== adhocId) return;
    let cancelled = false;
    getAdhocPack(saved.packCode, saved.ownerToken)
      .then((p) => {
        if (cancelled) return;
        if (p.scans_remaining <= 0) {
          clearSavedAdhocPack();
          setScanCreditsRemaining(null);
          return;
        }
        setScanCreditsRemaining(packCreditsRemaining(p));
      })
      .catch(() => { /* keep last known count */ });
    return () => {
      cancelled = true;
    };
  }, [stage, adhocId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const packProviderRaw = params.get('pack_provider');
    const packProvider = packProviderRaw === 'hitpay' ? 'hitpay' : 'stripe';

    if (packProvider === 'hitpay') {
      const hitpayStatus = (params.get('status') || '').toLowerCase();
      const packCheckout = params.get('reference') ?? params.get('pack_checkout');

      if (packCheckout && hitpayStatus === 'completed') {
        const pollKey = `${packProvider}:${packCheckout}`;
        if (packStripeReturnHandledRef.current === pollKey) return;
        packStripeReturnHandledRef.current = pollKey;
        syncDisplaySearch(stripPaymentQueryParams(location.search));
        setStage('pack_confirming');
        startPackConfirmPoll(packCheckout, packProvider);
        return;
      }

      if (packCheckout || hitpayStatus || packProviderRaw === 'hitpay') {
        const abandonKey = `hitpay-abandon:${packCheckout || hitpayStatus || 'return'}`;
        if (packStripeReturnHandledRef.current === abandonKey) return;
        packStripeReturnHandledRef.current = abandonKey;
        syncDisplaySearch(stripPaymentQueryParams(location.search));
        setStage('landing');
        return;
      }
    }

    const packPayment = params.get('pack_payment');
    const packCheckout = params.get('pack_checkout');

    if (packCheckout && packPayment === 'success') {
      const pollKey = `${packProvider}:${packCheckout}`;
      if (packStripeReturnHandledRef.current === pollKey) return;
      packStripeReturnHandledRef.current = pollKey;
      syncDisplaySearch(stripPaymentQueryParams(location.search));
      setStage('pack_confirming');
      startPackConfirmPoll(packCheckout, packProvider);
      return;
    }

    if (packPayment === 'cancelled') {
      if (packStripeReturnHandledRef.current === 'cancelled') return;
      packStripeReturnHandledRef.current = 'cancelled';
      clearPendingPackCheckout();
      syncDisplaySearch(stripPaymentQueryParams(location.search));
      setPayError('Pack purchase was cancelled.');
      setStage('landing');
      return;
    }

    const pack = params.get('pack');
    if (pack && !params.get('session') && !params.get('payment') && !params.get('pack_access')) {
      const inScanFlow = stage === 'disclaimer' || stage === 'profile'
        || stage === 'scan_instructions' || stage === 'scanning'
        || stage === 'confirming_payment';
      if (inScanFlow) return;
      if (stage === 'pack_hub' && packCode === pack) return;
      if (packHubOpeningRef.current === pack) return;
      if (packOpenBlockedRef.current === pack) return;
      if (stage === 'landing' || stage === 'pack_hub') {
        openPackHub(pack);
      }
    }
  }, [location.search, openPackHub, packCode, stage, startPackConfirmPoll, syncDisplaySearch]);

  // Restore paid flow after accidental refresh (no Stripe return params in URL).
  useEffect(() => {
    if (sessionRecoveryAttemptedRef.current) return;
    sessionRecoveryAttemptedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    if (params.get('session') && params.get('payment')) return;

    const snap = readAdhocFlowSnapshot();
    if (!snap || snap.adhocId !== adhocId) return;

    let cancelled = false;

    (async () => {
      try {
        const s = await getAdhocSession(snap.sessionId);
        if (cancelled) return;

        if (s.is_expired) {
          clearAdhocFlowSnapshot();
          return;
        }

        paymentReturnHandledRef.current = true;
        setSessionId(snap.sessionId);
        setSessionEmail(s.email ?? snap.sessionEmail ?? null);
        setGuestName(s.guest_name ?? snap.guestName ?? '');
        touchActivity();

        if (s.status === 'scanned' || s.status === 'report_sent') {
          setStage('email_success');
          if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
          resetTimerRef.current = setTimeout(() => {
            resetToLanding();
          }, ADHOC_DONE_RESET_MS);
          return;
        }

        if (s.status === 'paid') {
          const hp = snap.healthProfile ?? sessionToHealthProfile(s);
          if (hp) setHealthProfile(hp);

          if (ADHOC_SCAN_MAINTENANCE) {
            setStage('maintenance');
            return;
          }

          if (snap.stage === 'scanning' && s.guest_name && s.email) {
            setAwaitingCameraEnable(true);
            setScanInitKey(k => k + 1);
            setStage('scanning');
          } else if (snap.stage === 'scan_instructions') {
            setStage('scan_instructions');
          } else if (snap.stage === 'profile') {
            setStage('profile');
          } else if (snap.stage === 'disclaimer' || snap.stage === 'confirming_payment') {
            setStage('disclaimer');
          } else if (s.guest_name && s.email) {
            setStage('scan_instructions');
          } else {
            setStage('disclaimer');
          }
          return;
        }

        clearAdhocFlowSnapshot();
      } catch {
        /* corrupt storage or network blip — start fresh */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adhocId, resetToLanding, touchActivity]);

  useEffect(() => {
    if (!ADHOC_SCAN_MAINTENANCE) return;
    if (
      stage === 'disclaimer'
      || stage === 'profile'
      || stage === 'scan_instructions'
      || stage === 'scanning'
    ) {
      setStage('maintenance');
    }
  }, [stage]);

  // Persist active paid session so refresh can resume without re-payment.
  useEffect(() => {
    if (!sessionId) return;

    const persistStages: PersistedStage[] = ['confirming_payment', 'disclaimer', 'profile', 'scan_instructions', 'scanning'];
    if (!persistStages.includes(stage as PersistedStage)) return;

    writeAdhocFlowSnapshot({
      sessionId,
      adhocId,
      stage: stage as PersistedStage,
      sessionEmail: sessionEmail ?? undefined,
      guestName: guestName || undefined,
      healthProfile: healthProfile ?? undefined,
    });
  }, [sessionId, adhocId, stage, sessionEmail, guestName, healthProfile]);

  // Warn before leaving during an active paid session.
  useEffect(() => {
    const guarded: Stage[] = ['confirming_payment', 'disclaimer', 'profile', 'scan_instructions', 'scanning'];
    if (!guarded.includes(stage)) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [stage]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    setAdhocLogBooth(adhocId);
    adhocLog('app', 'info', 'Display app mounted', { adhocId });
  }, [adhocId]);

  useEffect(() => {
    adhocLog('app', 'info', `Stage → ${stage}`, { sessionId: sessionId ?? undefined });
  }, [stage, sessionId]);

  useEffect(() => {
    setAdhocLogSession(sessionId, adhocId);
  }, [sessionId, adhocId]);

  useEffect(() => {
    getAdhocConfig(adhocId)
      .then(setBoothConfig)
      .catch(() => {
        setBoothConfig({
          adhoc_id: adhocId,
          name: 'MyWellfie Booth',
          price_cents: 300,
          compare_at_cents: 1500,
          currency: 'usd',
          price_display: 'USD $3',
          compare_at_display: 'USD $15',
          discount_label: '80% OFF',
        });
      });
  }, [adhocId]);

  useEffect(() => {
    idleCheckRef.current = setInterval(() => {
      if (stage === 'landing' || stage === 'email_success' || stage === 'payment_failed'
        || stage === 'confirming_payment' || stage === 'pack_hub' || stage === 'pack_confirming') return;
      if (Date.now() - lastActivityRef.current > ADHOC_IDLE_TIMEOUT_MS) {
        setFlowError('This session timed out due to inactivity. Starting fresh for the next guest.');
        resetToLanding();
      }
    }, 60_000);
    return () => {
      if (idleCheckRef.current) clearInterval(idleCheckRef.current);
    };
  }, [stage, resetToLanding]);

  const handlePackStartScan = useCallback(async () => {
    if (!packCode || paying) return;
    setPaying(true);
    setPayError(null);
    touchActivity();
    try {
      const ownerToken = resolveOwnerTokenForPack(packCode, adhocId);
      const session = await createAdhocSession(adhocId, packCode, ownerToken);
      setSessionId(session.id);
      if (session.email) setSessionEmail(session.email);
      if (session.guest_name) setGuestName(session.guest_name);
      writeAdhocFlowSnapshot({
        sessionId: session.id,
        adhocId,
        stage: 'disclaimer',
      });
      setStage(ADHOC_SCAN_MAINTENANCE ? 'maintenance' : 'disclaimer');
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail;
      setPayError(typeof detail === 'string' ? detail : 'Could not start scan from pack.');
      try {
        const ownerToken = resolveOwnerTokenForPack(packCode, adhocId);
        await refreshPackInfo(packCode, ownerToken);
      } catch { /* ignore */ }
    } finally {
      setPaying(false);
    }
  }, [adhocId, packCode, paying, refreshPackInfo, touchActivity]);

  const handleDisclaimerContinue = useCallback(async () => {
    if (!sessionId) return;
    setDisclaimerSaving(true);
    setDisclaimerError(null);
    touchActivity();
    try {
      await acceptAdhocDisclaimer(sessionId, ADHOC_DISCLAIMER_VERSION);
      setStage('profile');
    } catch (e: unknown) {
      adhocLog('app', 'error', 'Disclaimer accept failed', {
        error: e instanceof Error ? e.message : String(e),
      });
      const detail = (e as { detail?: string })?.detail;
      setDisclaimerError(typeof detail === 'string' ? detail : 'Could not save your agreement. Please try again.');
    } finally {
      setDisclaimerSaving(false);
    }
  }, [sessionId, touchActivity]);

  const handleProfileContinue = useCallback(async (data: AdhocProfileData) => {
    if (!sessionId) return;
    setProfileSaving(true);
    setProfileError(null);
    touchActivity();
    try {
      await updateAdhocProfile(sessionId, {
        guest_name: data.guest_name,
        email: data.email,
        sex: data.sex,
        age: data.age,
        height_cm: data.height_cm,
        weight_kg: data.weight_kg,
        smoking_status: data.smoking_status,
      });
      if (data.email) setSessionEmail(data.email);
      if (data.guest_name) setGuestName(data.guest_name);
      setHealthProfile(adhocProfileToHealthProfile(data));
      setStartMeasuring(false);
      setIsLoading(false);
      setVideoReady(false);
      setCameraId(undefined);
      savedRef.current = false;
      setScanSaveError(null);
      setStage('scan_instructions');
    } catch (e: unknown) {
      adhocLog('app', 'error', 'Profile save failed', {
        error: e instanceof Error ? e.message : String(e),
      });
      const detail = (e as { detail?: string })?.detail;
      setProfileError(typeof detail === 'string' ? detail : 'Could not save your details. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  }, [sessionId, touchActivity]);

  const handleInstructionsContinue = useCallback(async () => {
    if (ADHOC_SCAN_MAINTENANCE) {
      setStage('maintenance');
      return;
    }
    touchActivity();
    setInstructionsContinuing(true);
    try {
      setStartMeasuring(false);
      setIsLoading(false);
      setVideoReady(false);
      setCameraId(undefined);
      // Request camera on user gesture (I'm ready tap) before opening scan UI.
      const devices = await refreshCameras();
      const firstCamera = devices.find((d) => d.deviceId);
      if (firstCamera?.deviceId) {
        setCameraId(firstCamera.deviceId);
        setAwaitingCameraEnable(false);
      } else {
        setAwaitingCameraEnable(true);
      }
      setScanInitKey((k) => k + 1);
      setStage('scanning');
    } catch (e: unknown) {
      adhocLog('app', 'error', 'Camera enable failed before scan', {
        error: e instanceof Error ? e.message : String(e),
      });
      setAwaitingCameraEnable(true);
      setScanInitKey((k) => k + 1);
      setStage('scanning');
    } finally {
      setInstructionsContinuing(false);
    }
  }, [touchActivity, refreshCameras]);

  const goToEmailSuccess = useCallback(() => {
    touchActivity();
    setStage('email_success');
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      if (packCode) {
        returnToPackHub(packCode);
      } else {
        resetToLanding();
      }
    }, ADHOC_DONE_RESET_MS);
  }, [packCode, resetToLanding, returnToPackHub, touchActivity]);

  const sendReportEmail = useCallback(async (sid: string, email?: string | null) => {
    if (!email) {
      throw new Error('No email on file. Please start over and enter your email.');
    }
    await sendAdhocReport(sid, email);
  }, []);

  const handleScanComplete = useCallback((
    vitalSignsResults: unknown,
    enabledVitalSigns?: EnabledVitalSigns,
  ) => {
    if (savedRef.current || !sessionId) return;
    savedRef.current = true;
    setSaving(true);
    setScanSaveError(null);
    setEmailSendError(null);
    setPostScanMessage('Saving your results…');
    touchActivity();

    const vitals = (vitalSignsResults as { results?: unknown })?.results ?? vitalSignsResults;

    const reportEmail = sessionEmail;

    saveAdhocScan(sessionId, vitals as Record<string, unknown>, {
      scan_platform: 'adhoc',
      measurement_duration_sec: processingTime,
      vitals_enabled: serializeEnabledVitals(enabledVitalSigns),
    })
      .then(async () => {
        adhocLog('app', 'info', 'Scan saved — sending report');
        setPostScanMessage('Sending your report…');
        if (packCode) {
          try {
            const ownerToken = resolveOwnerTokenForPack(packCode, adhocId);
            await refreshPackInfo(packCode, ownerToken);
          } catch { /* ignore */ }
        }
        await sendReportEmail(sessionId, reportEmail);
        setSaving(false);
        goToEmailSuccess();
      })
      .catch((e: unknown) => {
        adhocLog('app', 'error', 'Post-scan flow failed', {
          error: e instanceof Error ? e.message : String(e),
        });
        setSaving(false);
        const detail = (e as { detail?: string | { message?: string } })?.detail;
        const message = typeof detail === 'string'
          ? detail
          : typeof detail === 'object' && detail?.message
            ? detail.message
            : (e as Error)?.message || 'Something went wrong. Please try again.';

        getAdhocSession(sessionId)
          .then((s) => {
            if (s.status === 'scanned' || s.status === 'report_sent') {
              setEmailSendError(message);
            } else {
              savedRef.current = false;
              setScanSaveError('We could not save your scan results. Please try again or start over.');
            }
          })
          .catch(() => {
            savedRef.current = false;
            setScanSaveError('We could not save your scan results. Please try again or start over.');
          });
      });
  }, [adhocId, goToEmailSuccess, packCode, processingTime, refreshPackInfo, sendReportEmail, sessionEmail, sessionId, touchActivity]);

  const retryEmailSend = useCallback(async () => {
    if (!sessionId) return;
    const reportEmail = sessionEmail;
    if (!reportEmail) return;
    setEmailSendError(null);
    setSaving(true);
    setPostScanMessage('Sending your report…');
    touchActivity();
    try {
      await sendReportEmail(sessionId, reportEmail);
      setSaving(false);
      goToEmailSuccess();
    } catch (e: unknown) {
      adhocLog('app', 'error', 'Email retry failed', {
        error: e instanceof Error ? e.message : String(e),
      });
      setSaving(false);
      const detail = (e as { detail?: string | { message?: string } })?.detail;
      const message = typeof detail === 'string'
        ? detail
        : typeof detail === 'object' && detail?.message
          ? detail.message
          : 'Could not send email. Please try again or ask staff for help.';
      setEmailSendError(message);
    }
  }, [goToEmailSuccess, sendReportEmail, sessionEmail, sessionId, touchActivity]);

  const sdkUserInformation = useMemo(
    () => (healthProfile ? toSdkUserInformation(healthProfile) : undefined),
    [healthProfile],
  );

  const {
    sessionState, isMonitorReady, vitalSigns, finalReport,
    error, warning, info, clearScanAlert, retrySession,
  } = useAdhocMonitor({
    video, cameraId, processingTime, licenseKey,
    startMeasuring,
    shouldInitSdk: shouldPreWarmSdk,
    shouldInitCamera: stage === 'scanning',
    scanInitKey,
    userInformation: sdkUserInformation,
    onScanComplete: handleScanComplete,
  });

  useEffect(() => {
    setIsLicenseValid(isMonitorReady);
  }, [isMonitorReady]);

  const { scanError, scanWarning, clearScanWarning } = useResolvedScanAlert(error, warning);

  useEffect(() => {
    const validCameras = cameras?.filter(c => c.deviceId);
    if (!validCameras?.length) return;
    setCameraId(prev => prev ?? validCameras[0].deviceId);
  }, [cameras]);

  useEffect(() => {
    if (stage === 'scanning' && cameraId) {
      setAwaitingCameraEnable(false);
    }
  }, [stage, cameraId]);

  const handleEnableCamera = useCallback(async () => {
    touchActivity();
    adhocLog('app', 'info', 'User requested camera access');
    const devices = await refreshCameras();
    const firstCamera = devices.find((d) => d.deviceId);
    if (firstCamera?.deviceId) {
      setCameraId(firstCamera.deviceId);
      setAwaitingCameraEnable(false);
      return;
    }
    setAwaitingCameraEnable(true);
  }, [refreshCameras, touchActivity]);

  useEffect(() => {
    if (sessionState === SessionState.MEASURING) {
      setIsLoading(false);
      setIsMeasurementEnabled(!scanError);
      if (!isPageVisible) setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE || sessionState === SessionState.TERMINATED) && scanError
    ) {
      setIsMeasurementEnabled(false);
    }
    if (scanError && startMeasuring) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
    if (
      startMeasuring
      && (sessionState === SessionState.TERMINATED || sessionState === undefined)
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
  }, [scanError, sessionState, isPageVisible, startMeasuring]);

  const isInitialisingCamera = stage === 'scanning' && !scanError
    && sessionState !== SessionState.ACTIVE
    && (!camerasReady || !cameraId);
  const isPreparingScanner = stage === 'scanning' && !scanError
    && sessionState === undefined && camerasReady && Boolean(cameraId);
  const showCameraLoading = !awaitingCameraEnable
    && (isPreparingScanner || isInitialisingCamera) && !scanError;
  const canStartMeasure = !showCameraLoading && sessionState === SessionState.ACTIVE
    && Boolean(cameraId) && !scanError;

  const handleButtonClick = useCallback(() => {
    touchActivity();
    if (sessionState === SessionState.MEASURING) {
      clearTimeout(loadingTimeout);
      setStartMeasuring(false);
      setIsLoading(false);
      return;
    }
    if (!canStartMeasure) return;
    setIsLoading(true);
    setStartMeasuring(true);
    setLoadingTimeout(window.setTimeout(() => setIsLoading(true), processingTime * 1000));
  }, [canStartMeasure, processingTime, sessionState, loadingTimeout, touchActivity]);

  const handleAlertAction = useCallback((type: AlertActionType) => {
    touchActivity();
    if (type === 'retry') {
      clearScanAlert();
      setStartMeasuring(false);
      setIsLoading(false);
      refreshCameras?.().then(() => retrySession());
    } else if (type === 'dismiss') {
      clearScanWarning();
    }
  }, [clearScanAlert, clearScanWarning, refreshCameras, retrySession, touchActivity]);

  const openStaffSettings = useCallback(() => {
    if (ADHOC_STAFF_PIN) {
      setStaffPinPrompt(true);
      setStaffPinInput('');
    } else {
      setIsSettingsOpen(true);
    }
  }, []);

  const handleStaffCornerTap = useCallback(() => {
    const now = Date.now();
    staffTapTimesRef.current = [...staffTapTimesRef.current, now].filter(t => now - t < STAFF_TAP_WINDOW_MS);
    if (staffTapTimesRef.current.length >= STAFF_TAP_COUNT) {
      staffTapTimesRef.current = [];
      openStaffSettings();
    }
  }, [openStaffSettings]);

  const confirmStaffPin = useCallback(() => {
    if (staffPinInput === ADHOC_STAFF_PIN) {
      setStaffPinPrompt(false);
      setIsSettingsOpen(true);
    }
  }, [staffPinInput]);

  return (
    <div
      style={{ position: 'relative', width: '100%', minHeight: '100%', overflow: 'visible' }}
      onPointerDown={touchActivity}
    >
      <style>{SPIN_STYLE}</style>

      {/* Staff gesture — top-left corner */}
      <button
        type="button"
        aria-label="Staff"
        onClick={handleStaffCornerTap}
        style={{
          position: 'fixed', top: 0, left: 0, width: 72, height: 72,
          zIndex: 150, opacity: 0, cursor: 'default', border: 'none', background: 'transparent',
        }}
      />

      {stage === 'maintenance' && (
        <AdhocMaintenanceStage
          paidAfterCheckout={Boolean(sessionId)}
          sessionId={sessionId}
          onReturn={resetToLanding}
        />
      )}

      {stage === 'landing' && (
        <AdhocLandingStage
          adhocId={adhocId}
          scanCreditsRemaining={scanCreditsRemaining}
          onOpenMyScanCredits={handleOpenMyFamilyPack}
          singleScanHref={singleScanHref}
          multiScanHref={multiScanHref}
          priceDisplay={boothConfig?.price_display ?? 'USD $3'}
          compareAtDisplay={boothConfig?.compare_at_display ?? 'USD $15'}
          discountLabel={boothConfig?.discount_label ?? '80% OFF'}
          error={payError}
          scanMaintenance={ADHOC_SCAN_MAINTENANCE}
        />
      )}

      {stage === 'pack_hub' && packInfo && (
        <AdhocPackHubStage
          pack={packInfo}
          isOwner={!!packInfo.share_url}
          paying={paying}
          onStartScan={handlePackStartScan}
          onCopyLink={() => undefined}
          onBack={resetToLanding}
        />
      )}

      {stage === 'pack_confirming' && <AdhocPaymentConfirmStage />}

      {stage === 'confirming_payment' && <AdhocPaymentConfirmStage />}

      {stage === 'payment_failed' && (
        <AdhocPaymentFailedStage
          onReturn={resetToLanding}
          resetSeconds={ADHOC_PAYMENT_FAIL_RESET_MS / 1000}
        />
      )}

      {stage === 'disclaimer' && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20, overflowY: 'auto' }}>
          <AdhocDisclaimerStage
            onContinue={handleDisclaimerContinue}
            saving={disclaimerSaving}
            error={disclaimerError}
          />
        </div>
      )}

      {stage === 'profile' && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20, overflowY: 'auto' }}>
          <AdhocProfileStage
            sessionEmail={sessionEmail}
            onContinue={handleProfileContinue}
            saving={profileSaving}
            error={profileError}
          />
        </div>
      )}

      {stage === 'scan_instructions' && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20, overflowY: 'auto' }}>
          <AdhocInstructionsStage
            onContinue={handleInstructionsContinue}
            continuing={instructionsContinuing}
          />
        </div>
      )}

      {stage === 'scanning' && (
        <>
          {saving ? (
            <div style={{ minHeight: '100dvh', background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 44, height: 44, border: '4px solid rgba(20,184,166,0.2)', borderTopColor: '#14b8a6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
              <p style={{ color: '#64748b', fontSize: 16, fontWeight: 600 }}>{postScanMessage}</p>
            </div>
          ) : (
            <AdhocScanStage
              videoRef={video}
              maskUrl={Mask}
              isDesktop={desktop}
              showCameraLoading={showCameraLoading}
              isPreparingScanner={isPreparingScanner}
              needsCameraEnable={awaitingCameraEnable}
              onEnableCamera={handleEnableCamera}
              measuring={sessionState === SessionState.MEASURING}
              processingTime={processingTime}
              showCameraHint={
                !startMeasuring
                && sessionState !== SessionState.MEASURING
                && !scanError
                && !showCameraLoading
                && !awaitingCameraEnable
              }
              scanError={scanError}
              scanWarning={scanWarning}
              info={info}
              isLoading={isLoading}
              canStartMeasure={canStartMeasure}
              onButtonClick={handleButtonClick}
              onAlertAction={handleAlertAction}
              setVideoReady={setVideoReady}
            />
          )}
        </>
      )}

      {stage === 'email_success' && sessionEmail && (
        <AdhocEmailSuccessStage
          email={sessionEmail}
          guestName={guestName}
          onReturn={resetToLanding}
          resetSeconds={ADHOC_DONE_RESET_MS / 1000}
        />
      )}

      {scanSaveError && (
        <AdhocErrorPanel
          title="Scan save failed"
          message={scanSaveError}
          primaryLabel="Try Again"
          onPrimary={() => setScanSaveError(null)}
          secondaryLabel="Start Over"
          onSecondary={resetToLanding}
        />
      )}

      {emailSendError && (
        <AdhocErrorPanel
          title="Could not send report"
          message={emailSendError}
          primaryLabel="Retry Email"
          onPrimary={retryEmailSend}
          secondaryLabel="Start Over"
          onSecondary={resetToLanding}
        />
      )}

      {flowError && (
        <AdhocErrorPanel
          title="Session reset"
          message={flowError}
          primaryLabel="OK"
          onPrimary={() => setFlowError(null)}
        />
      )}

      {staffPinPrompt && (
        <AdhocErrorPanel
          title="Staff access"
          message="Enter staff PIN to open camera settings."
          primaryLabel="Unlock"
          onPrimary={confirmStaffPin}
          secondaryLabel="Cancel"
          onSecondary={() => setStaffPinPrompt(false)}
        />
      )}

      {staffPinPrompt && (
        <div style={{ position: 'fixed', top: '42%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 301 }}>
          <input
            type="password"
            value={staffPinInput}
            onChange={(e) => setStaffPinInput(e.target.value)}
            placeholder="PIN"
            style={{ padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', width: 200, textAlign: 'center' }}
          />
        </div>
      )}

      <AdhocSettingsBars
        open={isSettingsOpen}
        onClose={({ cameraId: cid }) => { setCameraId(cid); setIsSettingsOpen(false); }}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
        onDownloadLog={() => {
          adhocLog('app', 'info', 'Staff downloaded client debug log');
          downloadAdhocDebugLog();
        }}
      />
    </div>
  );
}
